import type { Middleware, MiddlewareCallbackParams } from 'openapi-fetch'

/**
 * Attaches `X-Device-Id` to every outgoing request.
 *
 * The backend's session-revoke rollout makes the header mandatory on every
 * authenticated request once both clients send it; before this, web sent it
 * only on `POST /users/me/session`.
 *
 * The id is asked for per request rather than captured at construction: the
 * `deviceId.ts` memo is per page load, so a provider read here still returns
 * one stable value within a session, while a reload may legitimately mint a new
 * one. A provider that throws costs the header, never the request — the same
 * contract `deviceId.ts` states: failing to identify the device must not become
 * a failure to do anything else.
 *
 * Declared like `AuthMiddleware`: `onRequest` over `Pick<…, 'request'>` and
 * non-optional so tests call it with `{ request }` and need no `!`.
 */
export type DeviceMiddleware = Middleware & {
  onRequest: (params: Pick<MiddlewareCallbackParams, 'request'>) => Promise<Request>
}

export function createDeviceMiddleware(getId: () => string): DeviceMiddleware {
  return {
    async onRequest({ request }) {
      try {
        request.headers.set('X-Device-Id', getId())
      } catch {
        // Deliberately swallowed — see the docblock.
      }
      return request
    },
  }
}
